import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, ArrowLeft, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';

// Cape Town Edit style tokens
const CT = {
  bg: '#0a0a0f',
  panel: 'rgba(255,255,255,0.02)',
  cream: '#E8E3D9',
  secondary: '#b0b4c0',
  border: 'rgba(255,255,255,0.06)',
  gold: '#C9A84C',
  goldDim: 'rgba(201,168,76,0.25)',
  serif: "'Cormorant Garamond', serif",
  mono: "'DM Mono', monospace",
};

function CTTagPill({ tag }) {
  return (
    <span style={{ fontFamily: CT.mono, fontSize: 10, color: CT.gold, border: `1px solid ${CT.goldDim}`, padding: '2px 8px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{tag}</span>
  );
}

function CTEntry({ entry }) {
  return (
    <div style={{ padding: '24px 0', borderBottom: `1px solid ${CT.border}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <p style={{ fontFamily: CT.serif, fontSize: 26, color: CT.cream, lineHeight: 1.2, margin: 0 }}>{entry.name}</p>
        {entry.url && (
          <a href={entry.url} target="_blank" rel="noopener noreferrer"
            style={{ color: CT.goldDim, marginTop: 6, flexShrink: 0, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = CT.gold}
            onMouseLeave={e => e.currentTarget.style.color = CT.goldDim}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        )}
      </div>
      {entry.note && (
        <p style={{ fontFamily: CT.serif, fontSize: 16, fontStyle: 'italic', color: CT.secondary, lineHeight: 1.75, marginTop: 8 }}>{entry.note}</p>
      )}
      {entry.tags && entry.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {entry.tags.map(t => <CTTagPill key={t} tag={t} />)}
        </div>
      )}
    </div>
  );
}

function CTSection({ section }) {
  const entries = [...(section.entries || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div style={{ background: CT.panel, border: `1px solid ${CT.border}`, borderTop: `2px solid ${CT.gold}`, marginBottom: 24, overflow: 'hidden' }}>
      {section.photo_url && (
        <div style={{ position: 'relative', height: 300, overflow: 'hidden' }}>
          <img src={section.photo_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(17,17,17,0.95) 100%)' }} />
        </div>
      )}
      <div style={{ padding: '20px 28px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: CT.mono, fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: CT.gold }}>{section.title}</span>
        <div style={{ flex: 1, height: 1, background: CT.goldDim }} />
      </div>
      <div style={{ padding: '0 28px 8px' }}>
        {entries.map(entry => <CTEntry key={entry.id} entry={entry} />)}
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME_COLORS = {
  PURPOSE: '#c9a84c', INTEGRATION: '#7eb8c9', HARDSHIP: '#b87d7d', HONESTY: '#c97eb8',
  FREEDOM: '#7ec9a0', DISCIPLINE: '#c98e7e', ALIVENESS: '#a07ec9', SOVEREIGNTY: '#7e9ec9',
  DISCERNMENT: '#c9c47e', LOVE: '#c97e8f', AUTHENTICITY: '#7ec9c4', AGENCY: '#c9b47e',
  INDIFFERENCE: '#9ec97e', NARRATIVE: '#8f7ec9', 'THE THINKER': '#e8e2d9',
};

// Werner's paintings - visual assets for Cult of One
const PAINTINGS = [
  { id: 'diagonal', url: '/paintings/diagonal.jpg', mood: 'silver' },
  { id: 'cross-silver', url: '/paintings/cross-silver.jpg', mood: 'silver' },
  { id: 'chaos', url: '/paintings/chaos.jpg', mood: 'monochrome' },
  { id: '777', url: '/paintings/777.jpg', mood: 'gold' },
  { id: 'explosion', url: '/paintings/explosion.jpg', mood: 'vibrant' },
  { id: 'x-blue', url: '/paintings/x-blue.jpg', mood: 'silver' },
  { id: 'x-gold', url: '/paintings/x-gold.jpg', mood: 'gold' },
  { id: 'field', url: '/paintings/field.jpg', mood: 'gold' },
];

// Seeded random based on day - rotates daily
const getDaysSeed = () => {
  const now = new Date();
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

const seededShuffle = (arr, seed) => {
  const shuffled = [...arr];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Visual styles for paintings - rotated daily
const PAINTING_STYLES = [
  { type: 'card', className: 'col-span-1', aspectRatio: 'aspect-[4/3]' },
  { type: 'wide', className: 'md:col-span-2', aspectRatio: 'aspect-[16/7]' },
  { type: 'accent', className: 'col-span-1', aspectRatio: 'aspect-square', opacity: 0.6 },
  { type: 'separator', className: 'md:col-span-3', aspectRatio: 'aspect-[21/6]' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Painting visual component
function PaintingVisual({ painting, style }) {
  if (style.type === 'separator') {
    return (
      <div className={`${style.className} overflow-hidden group`}>
        <div className={`${style.aspectRatio} relative`}>
          <img src={painting.url} alt="" loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-[1.02] transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
        </div>
      </div>
    );
  }
  
  if (style.type === 'accent') {
    return (
      <div className={`${style.className} bg-white/[0.02] border border-white/[0.06] overflow-hidden relative group transition-all duration-300 hover:border-[#b0b4c0] hover:scale-[1.02]`}>
        <div className={`${style.aspectRatio}`}>
          <img src={painting.url} alt="" loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-all duration-700 grayscale group-hover:grayscale-0 group-hover:scale-[1.02]" />
        </div>
      </div>
    );
  }
  
  // Card or wide style
  return (
    <div className={`${style.className} bg-[#0a0a0f] overflow-hidden group transition-all duration-300 hover:scale-[1.02]`}>
      <div className={`${style.aspectRatio} relative`}>
        <img src={painting.url} alt="" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700 group-hover:scale-[1.02]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </div>
  );
}

function CuratedPasswordModal({ onSuccess, onClose }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');

  const attempt = () => {
    if (pw === 'thisisafrica') {
      sessionStorage.setItem('curated_unlocked', '1');
      onSuccess();
    } else {
      setError('incorrect password');
      setPw('');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', padding: 40, width: '90%', maxWidth: 360, position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#b0b4c0', cursor: 'pointer' }}><X size={16} /></button>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: '#E8E3D9', marginBottom: 24 }}>The Cape Town Edit</p>
        <input
          type="password"
          value={pw}
          onChange={e => { setPw(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && attempt()}
          placeholder="password"
          autoFocus
          style={{ width: '100%', background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.06)', color: '#E8E3D9', padding: '10px 14px', fontFamily: "'DM Mono', monospace", fontSize: 13, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}
        />
        {error && <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#b87d7d', letterSpacing: '0.1em', marginBottom: 8 }}>{error}</p>}
        <button
          onClick={attempt}
          style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#E8E3D9', padding: '10px', fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', marginTop: 4 }}>
          Enter
        </button>
      </div>
    </div>
  );
}

export default function CultGallery() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mantras, setMantras] = useState([]);
  const [essays, setEssays] = useState([]);
  const [poems, setPoems] = useState([]);
  const [paintings, setPaintings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Curated password gate + guide data
  const [showCuratedModal, setShowCuratedModal] = useState(false);
  const [curatedGuide, setCuratedGuide] = useState(null);
  const [activeCuratedGuide, setActiveCuratedGuide] = useState(false);

  // Tab state — initialise from ?tab= query param
  const [galleryTab, setGalleryTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['mantras', 'essays', 'poems', 'paintings', 'curated'].includes(t) ? t : 'mantras';
  });

  // Detail view state
  const [activeMantra, setActiveMantra] = useState(null);
  const [activeEssay, setActiveEssay] = useState(null);
  const [activePoem, setActivePoem] = useState(null);

  useEffect(() => {
    document.title = 'Cult of One | Werner du Toit';
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [mantrasRes, essaysRes, poemsRes, paintingsRes, curatedRes] = await Promise.all([
        axios.get(`${API}/public/mantras`),
        axios.get(`${API}/public/essays`),
        axios.get(`${API}/public/poems`),
        axios.get(`${API}/public/cult/paintings`),
        axios.get(`${API}/curated/capetown`).catch(() => ({ data: null })),
      ]);
      setMantras(mantrasRes.data);
      setEssays(essaysRes.data);
      setPoems(poemsRes.data);
      setPaintings(paintingsRes.data);
      setCuratedGuide(curatedRes.data);
    } catch (err) {
      console.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const openMantra = (m) => {
    setActiveMantra(m);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const closeMantra = () => setActiveMantra(null);

  const openEssay = (e) => {
    setActiveEssay(e);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const closeEssay = () => setActiveEssay(null);

  const openPoem = (p) => {
    setActivePoem(p);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const closePoem = () => setActivePoem(null);

  // Prepare paintings for display - shuffled daily
  const daysSeed = getDaysSeed();
  const shuffledPaintings = seededShuffle(PAINTINGS, daysSeed);
  const shuffledStyles = seededShuffle(PAINTING_STYLES, daysSeed + 1);
  
  // Interleave mantras with paintings
  const buildGridItems = () => {
    const items = [];
    let paintingIdx = 0;
    let styleIdx = 0;
    
    // Add a hero separator painting at the top
    items.push({ type: 'painting', painting: shuffledPaintings[paintingIdx % shuffledPaintings.length], style: PAINTING_STYLES[3] });
    paintingIdx++;
    
    mantras.forEach((m, i) => {
      items.push({ type: 'mantra', mantra: m, index: i });
      
      // Insert painting every 3-4 mantras with varied placement
      if ((i + 1) % 3 === 0 && paintingIdx < shuffledPaintings.length + 2) {
        const style = shuffledStyles[styleIdx % shuffledStyles.length];
        items.push({ 
          type: 'painting', 
          painting: shuffledPaintings[paintingIdx % shuffledPaintings.length], 
          style 
        });
        paintingIdx++;
        styleIdx++;
      }
    });
    
    return items;
  };
  
  const gridItems = buildGridItems();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-8 h-8 rounded-lg bg-[#6B35B8] animate-pulse" />
      </div>
    );
  }

  // ==================== MANTRA DETAIL VIEW ====================
  if (activeMantra) {
    const color = THEME_COLORS[activeMantra.theme] || '#b0b4c0';
    const currentIndex = mantras.findIndex(m => m.id === activeMantra.id);
    
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20 md:pb-8" data-testid="cult-gallery-mantra-detail" style={{ fontFamily: "'Georgia', serif" }}>
        <PublicNav />
        
        <div className="px-6 md:px-10 pt-8 max-w-[760px] mx-auto">
          <button data-testid="cult-gallery-back" onClick={closeMantra}
            className="border border-white/[0.06] px-5 py-2.5 mb-12 text-xs tracking-[0.2em] uppercase hover:border-[#6b7084] hover:text-[#f0f0f5] transition-colors flex items-center gap-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
            <ArrowLeft size={12} />
            All Mantras
          </button>

          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color, opacity: 0.8 }}>
            {activeMantra.theme}
          </p>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-[1.3] mb-3 pl-6 border-l-4"
            style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5', borderColor: color }}
            data-testid="mantra-gallery-title">
            {activeMantra.title || activeMantra.text || 'Untitled'}
          </h2>
          {activeMantra.subtitle && (
            <p className="text-base pl-6 italic mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
              {activeMantra.subtitle}
            </p>
          )}
          
          {activeMantra.attribution && (
            <p className="text-sm pl-6 mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
              - {activeMantra.attribution}
            </p>
          )}

          <p className="text-sm tracking-[0.3em] uppercase mb-6 mt-10" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
            Voices
          </p>

          <div className="space-y-8" data-testid="mantra-gallery-quotes">
            {(activeMantra.quotes || []).length === 0 ? (
              <p className="text-sm italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
                No voices yet.
              </p>
            ) : (
              (activeMantra.quotes || []).map((q, i) => (
                <div key={i} data-testid={`gallery-quote-${i}`} className="pl-6 border-l-2" style={{ borderColor: color }}>
                  {q.isPoem ? (
                    <p className="text-lg italic leading-[2.2] whitespace-pre-line" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#D8D2C8', fontSize: '1.125rem' }}>
                      {q.text}
                    </p>
                  ) : (
                    <p className="text-xl sm:text-2xl italic leading-[1.6]" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
                      "{q.text}"
                    </p>
                  )}
                  <p className="mt-3 text-xs tracking-[0.15em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color, opacity: 0.7 }}>
                    - {q.author}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-between mt-16 pb-12 pt-8 border-t border-white/[0.06]">
            {currentIndex > 0 ? (
              <button onClick={() => openMantra(mantras[currentIndex - 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Prev
              </button>
            ) : <span />}
            {currentIndex < mantras.length - 1 ? (
              <button onClick={() => openMantra(mantras[currentIndex + 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Next
              </button>
            ) : <span />}
          </div>
        </div>
      </div>
    );
  }

  // ==================== ESSAY DETAIL VIEW ====================
  if (activeEssay) {
    const currentIndex = essays.findIndex(e => e.id === activeEssay.id);
    
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20 md:pb-8" data-testid="cult-gallery-essay-detail" style={{ fontFamily: "'Georgia', serif" }}>
        <PublicNav />
        
        <div className="px-6 md:px-10 pt-8 max-w-[760px] mx-auto">
          <button data-testid="essay-back-btn" onClick={closeEssay}
            className="border border-white/[0.06] px-5 py-2.5 mb-12 text-xs tracking-[0.2em] uppercase hover:border-[#6b7084] hover:text-[#f0f0f5] transition-colors flex items-center gap-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
            <ArrowLeft size={12} />
            All Essays
          </button>

          <h2 className="text-3xl md:text-4xl font-bold leading-[1.3] mb-3"
            style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}
            data-testid="essay-detail-title">
            {activeEssay.title}
          </h2>
          
          {activeEssay.tagline && (
            <p className="text-base italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
              {activeEssay.tagline}
            </p>
          )}

          <div className="border-t border-white/[0.06] my-6" />

          <div className="text-lg leading-[1.9] whitespace-pre-wrap" 
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#f0f0f5' }}
            data-testid="essay-detail-body">
            {activeEssay.body}
          </div>

          <div className="flex justify-between mt-16 pb-12 pt-8 border-t border-white/[0.06]">
            {currentIndex > 0 ? (
              <button onClick={() => openEssay(essays[currentIndex - 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Prev
              </button>
            ) : <span />}
            {currentIndex < essays.length - 1 ? (
              <button onClick={() => openEssay(essays[currentIndex + 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Next
              </button>
            ) : <span />}
          </div>
        </div>
      </div>
    );
  }

  // ==================== POEM DETAIL VIEW ====================
  if (activePoem) {
    const currentIndex = poems.findIndex(p => p.id === activePoem.id);
    
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20 md:pb-8" data-testid="cult-gallery-poem-detail" style={{ fontFamily: "'Georgia', serif" }}>
        <PublicNav />
        
        <div className="px-6 md:px-10 pt-8 max-w-[760px] mx-auto">
          <button data-testid="poem-back-btn" onClick={closePoem}
            className="border border-white/[0.06] px-5 py-2.5 mb-12 text-xs tracking-[0.2em] uppercase hover:border-[#6b7084] hover:text-[#f0f0f5] transition-colors flex items-center gap-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
            <ArrowLeft size={12} />
            All Poems
          </button>

          <h2 className="text-3xl md:text-4xl font-bold leading-[1.3] mb-8 text-center"
            style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}
            data-testid="poem-detail-title">
            {activePoem.title}
          </h2>

          <div className="text-xl leading-[2.2] whitespace-pre-wrap text-center max-w-lg mx-auto italic" 
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#f0f0f5' }}
            data-testid="poem-detail-body">
            {activePoem.body}
          </div>

          <div className="flex justify-between mt-16 pb-12 pt-8 border-t border-white/[0.06]">
            {currentIndex > 0 ? (
              <button onClick={() => openPoem(poems[currentIndex - 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Prev
              </button>
            ) : <span />}
            {currentIndex < poems.length - 1 ? (
              <button onClick={() => openPoem(poems[currentIndex + 1])}
                className="text-[12px] hover:text-[#f0f0f5] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                Next
              </button>
            ) : <span />}
          </div>
        </div>
      </div>
    );
  }

  // ==================== CAPE TOWN EDIT INLINE VIEW ====================
  if (activeCuratedGuide && curatedGuide) {
    const sections = [...(curatedGuide.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      <div style={{ minHeight: '100vh', background: CT.bg, color: CT.cream }}>
        <PublicNav />
        {/* Hero */}
        <div style={{ position: 'relative', height: 480, overflow: 'hidden' }}>
          {curatedGuide.hero_image_url ? (
            <img src={curatedGuide.hero_image_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, #0a0a0f 100%)' }} />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(13,13,13,0.2) 0%, rgba(13,13,13,0.80) 100%)' }} />
          {/* Back button */}
          <button
            onClick={() => { setActiveCuratedGuide(false); setGalleryTab('curated'); window.scrollTo({ top: 0, behavior: 'instant' }); }}
            style={{ position: 'absolute', top: 20, left: 24, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.5)', border: `1px solid ${CT.border}`, color: CT.cream, padding: '8px 16px', fontFamily: CT.mono, fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer' }}>
            <ArrowLeft size={12} />
            Back
          </button>
          <div style={{ position: 'absolute', bottom: 48, left: 0, right: 0, padding: '0 40px', maxWidth: 760 }}>
            <h1 style={{ fontFamily: CT.serif, fontSize: 'clamp(36px, 7vw, 64px)', fontWeight: 300, color: CT.cream, margin: '0 0 12px', lineHeight: 1.05 }}>{curatedGuide.title}</h1>
            <p style={{ fontFamily: CT.mono, fontSize: 13, color: 'rgba(232,227,217,0.65)', letterSpacing: '0.12em' }}>{curatedGuide.subtitle}</p>
          </div>
        </div>
        {/* Credit bar */}
        <div style={{ padding: '16px 40px', borderBottom: `1px solid ${CT.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontFamily: CT.mono, fontSize: 10, color: CT.secondary, letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0 }}>A Zephyr OS production · wernerdutoit.co.za</p>
        </div>
        {/* Intro */}
        {curatedGuide.intro && (
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '64px 40px 0' }}>
            <p style={{ fontFamily: CT.serif, fontSize: 20, lineHeight: 1.8, color: CT.cream, fontStyle: 'italic' }}>{curatedGuide.intro}</p>
          </div>
        )}
        {/* Sections */}
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 0' }}>
          {sections.map(section => <CTSection key={section.id} section={section} />)}
        </div>
        {/* Footer */}
        <div style={{ maxWidth: 720, margin: '48px auto 0', padding: '64px 24px', borderTop: `1px solid ${CT.border}` }}>
          <p style={{ fontFamily: CT.mono, fontSize: 11, color: CT.secondary, letterSpacing: '0.15em', textTransform: 'uppercase', textAlign: 'center' }}>
            Curated by Werner du Toit · Cape Town · 2026 Edition · A Zephyr OS production
          </p>
        </div>
      </div>
    );
  }

  // ==================== GRID VIEW ====================
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20 md:pb-8" data-testid="cult-gallery-page" style={{ fontFamily: "'Georgia', serif" }}>
      <PublicNav />

      {/* Header */}
      <div className="border-b border-white/[0.06] px-6 md:px-10 pt-6 md:pt-8 pb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center mb-1" style={{ gap: 2 }}>
            <img src="/logo-cult.png" alt="Cult of One" style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0 }} />
            <span data-testid="cult-gallery-title" style={{ fontFamily: 'system-ui, sans-serif', fontSize: 28, fontWeight: 300, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>ULT OF ONE</span>
          </div>
          <p className="text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#b0b4c0', paddingLeft: 42 }}>
            Manifest of a Movement
          </p>
        </div>
        {/* Desktop: labels · Mobile: logo + name */}
        <div className="hidden md:block text-right" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 11, color: '#b0b4c0', letterSpacing: 2 }}>
          <div>CONGRUENCE · EXPANSION</div>
          <div>ALIVENESS · SOVEREIGNTY</div>
        </div>
        <a href="/labs" className="flex md:hidden items-center gap-2 text-decoration-none" style={{ textDecoration: 'none' }}>
          <img src="/logo-zephyr.png" alt="Zephyr OS" style={{ width: 28, height: 28, opacity: 0.8, clipPath: 'url(#squircle)' }} />
          <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 10, fontWeight: 300, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(240,236,228,0.45)' }}>Zephyr OS</span>
        </a>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 md:px-10 max-w-[1100px]">
        <div className="flex gap-8 mt-6 border-b border-white/[0.06]">
          {['mantras', 'essays', 'poems', 'paintings', 'curated'].map(tab => (
            <button
              key={tab}
              onClick={() => setGalleryTab(tab)}
              data-testid={`gallery-tab-${tab}`}
              className={`pb-3 text-sm tracking-[0.25em] uppercase transition-colors ${
                galleryTab === tab
                  ? 'text-[#9B6DD4] border-b border-[#9B6DD4] -mb-px'
                  : 'text-[#b0b4c0] hover:text-[#f0f0f5]'
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, letterSpacing: '1.5px' }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Mantras Tab */}
        {galleryTab === 'mantras' && (
          <>
            {mantras.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
                  No mantras to display.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2px] mt-6" data-testid="mantras-grid">
                {gridItems.map((item, idx) => {
                  if (item.type === 'painting') {
                    return <PaintingVisual key={`painting-${idx}`} painting={item.painting} style={item.style} />;
                  }
                  
                  const m = item.mantra;
                  const i = item.index;
                  const tc = THEME_COLORS[m.theme] || '#b0b4c0';
                  return (
                    <button
                      key={m.id || idx}
                      onClick={() => openMantra(m)}
                      data-testid={`gallery-mantra-${m.id}`}
                      className="text-left bg-white/[0.02] border border-white/[0.06] p-6 md:p-7 relative overflow-hidden group transition-all duration-300 hover:bg-white/[0.04] hover:border-[#b0b4c0] hover:scale-[1.02] cursor-pointer"
                    >
                      <div className="absolute top-0 left-0 right-0 h-[2px] transition-transform duration-400 origin-left scale-x-0 group-hover:scale-x-100"
                        style={{ background: tc }} />
                      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: tc, opacity: 0.3 }} />

                      <div className="flex justify-between items-start mb-4">
                        <span className="text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: tc, opacity: 0.8 }}>
                          {m.theme}
                        </span>
                      </div>

                      <p className="text-base leading-[1.7] mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
                        {m.title || m.text || 'Untitled'}
                      </p>
                      {m.subtitle && (
                        <p className="text-xs italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
                          {m.subtitle}
                        </p>
                      )}

                      <div className="mt-5 flex items-center gap-2">
                        <span className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084', letterSpacing: 1 }}>
                          {(m.quotes || []).length} {(m.quotes || []).length === 1 ? 'voice' : 'voices'}
                        </span>
                        <ChevronRight size={10} className="text-[#b0b4c0] group-hover:text-[#6b7084] transition-colors" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Enneagram footer - only on mantras tab */}
            <div className="mt-16 pb-12 pt-8 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { type: '7w8', label: 'The Trailblazer', desc: 'Adventurous, assertive, fiercely independent - chasing excitement while breaking through limits.' },
                { type: '8w7', label: 'The Maverick', desc: 'Bold, high-energy, unstoppable - driven to seize freedom and bend the world to their will.' },
              ].map(e => (
                <div key={e.type}>
                  <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
                    Enneagram {e.type}
                  </p>
                  <p className="text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>{e.label}</p>
                  <p className="text-sm italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>{e.desc}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Essays Tab */}
        {galleryTab === 'essays' && (
          <div className="mt-6">
            {essays.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
                  No essays to display.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2px]" data-testid="essays-grid">
                {essays.map((essay) => (
                  <button
                    key={essay.id}
                    onClick={() => openEssay(essay)}
                    data-testid={`gallery-essay-${essay.id}`}
                    className="text-left bg-white/[0.02] border border-white/[0.06] p-6 md:p-7 relative overflow-hidden group transition-all duration-300 hover:bg-white/[0.04] hover:border-[#b0b4c0] hover:scale-[1.02]"
                  >
                    <span className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
                      Essay
                    </span>
                    
                    <p className="text-base leading-[1.7] mt-3 mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
                      {essay.title}
                    </p>
                    
                    {essay.tagline && (
                      <p className="text-xs italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
                        {essay.tagline}
                      </p>
                    )}
                    
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-[10px] font-mono" style={{ color: '#b0b4c0' }}>
                        {formatDate(essay.created_at)}
                      </span>
                      <ChevronRight size={10} className="text-[#b0b4c0] group-hover:text-[#6b7084] transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Poems Tab */}
        {galleryTab === 'poems' && (
          <div className="mt-6">
            {poems.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
                  No poems to display.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2px]" data-testid="poems-grid">
                {poems.map((poem) => (
                  <button
                    key={poem.id}
                    onClick={() => openPoem(poem)}
                    data-testid={`gallery-poem-${poem.id}`}
                    className="text-left bg-white/[0.02] border border-white/[0.06] p-6 md:p-7 relative overflow-hidden group transition-all duration-300 hover:bg-white/[0.04] hover:border-[#b0b4c0] hover:scale-[1.02]"
                  >
                    <span className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
                      Poem
                    </span>
                    
                    <p className="text-base leading-[1.7] mt-3 mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
                      {poem.title}
                    </p>
                    
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-[10px] font-mono" style={{ color: '#b0b4c0' }}>
                        {formatDate(poem.created_at)}
                      </span>
                      <ChevronRight size={10} className="text-[#b0b4c0] group-hover:text-[#6b7084] transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Paintings Tab */}
        {galleryTab === 'paintings' && (
          <div className="mt-6">
            {paintings.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
                  No paintings to display.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[2px]">
                {paintings.map((painting) => (
                  <div key={painting.id} className="bg-[#0a0a0f] overflow-hidden group">
                    <div className="aspect-square relative">
                      <img
                        src={painting.url}
                        alt={painting.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
                      />
                    </div>
                    {painting.title && (
                      <p className="px-3 py-2 text-xs text-[#b0b4c0]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.05em' }}>{painting.title}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Curated Tab */}
        {galleryTab === 'curated' && (
          <div className="mt-6">
            <div
              className="max-w-sm border border-white/[0.06] overflow-hidden cursor-pointer hover:border-[#b0b4c0] transition-all duration-300 bg-white/[0.02]"
              onClick={() => {
                if (sessionStorage.getItem('curated_unlocked') === '1') {
                  setActiveCuratedGuide(true);
                  window.scrollTo({ top: 0, behavior: 'instant' });
                } else {
                  setShowCuratedModal(true);
                }
              }}>
              <div className="h-48 bg-[#0a0a0f] relative overflow-hidden">
                {curatedGuide?.hero_image_url && (
                  <img src={curatedGuide.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/[0.02]/80" />
              </div>
              <div className="p-5">
                <p className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#E8E3D9' }}>The Cape Town Edit</p>
                {curatedGuide?.subtitle && (
                  <p className="text-xs italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>{curatedGuide.subtitle}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showCuratedModal && (
        <CuratedPasswordModal
          onSuccess={() => { setShowCuratedModal(false); setActiveCuratedGuide(true); window.scrollTo({ top: 0, behavior: 'instant' }); }}
          onClose={() => setShowCuratedModal(false)}
        />
      )}
    </div>
  );
}
